import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  DAY_NAMES,
  DEFAULT_AUTO_REPLY,
  describeHours,
  formatMinute,
  parseMinute,
  validateAutoReply,
  type AutoReplyConfig,
} from '@shared/autoReply'
import { api, ApiError } from '../lib/api'
import { ReadOnlyNotice } from './ReadOnlyNotice'

/** Zones offered up front. Anything else can be typed. */
const ZONES = [
  'UTC',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Los_Angeles',
  'Australia/Sydney',
  'Africa/Lagos',
]

const KIND_LABELS: Record<string, string> = {
  greeting: 'first-time greeting',
  away: 'out of hours',
  keyword: 'keyword',
  knowledge: 'from the knowledge base',
  ai: 'drafted by AI',
}

/**
 * Replying without a person.
 *
 * The screen deliberately gives as much room to what stops a reply as to what sends one. Every
 * guard here exists because of a specific way this feature goes wrong — two responders talking
 * to each other overnight, a customer answered five times while still typing — and a customer
 * switching it on deserves to see that those exist before they find out.
 */
export function AutoReply() {
  const queryClient = useQueryClient()
  const data = useQuery({ queryKey: ['auto-reply'], queryFn: api.autoReply, retry: false })
  const [draft, setDraft] = useState<AutoReplyConfig | null>(null)

  const save = useMutation({
    mutationFn: (config: AutoReplyConfig) => api.saveAutoReply(config),
    onSuccess: () => {
      setDraft(null)
      void queryClient.invalidateQueries({ queryKey: ['auto-reply'] })
    },
  })

  if (data.isPending) return <div className="cad-card" style={{ height: '240px' }} />
  if (data.isError) {
    const message = data.error instanceof ApiError ? data.error.message : 'Unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const { activity, knowledgeCount, aiConfigured, canManage } = data.data!
  const config = draft ?? data.data!.config
  const problems = validateAutoReply(config)
  const dirty = draft !== null

  const set = (patch: Partial<AutoReplyConfig>) => setDraft({ ...config, ...patch })
  const setHours = (patch: Partial<AutoReplyConfig['hours']>) =>
    setDraft({ ...config, hours: { ...config.hours, ...patch } })

  const setWindow = (day: number, field: 'openMinute' | 'closeMinute', value: string) => {
    const minute = parseMinute(value)
    if (minute === null) return
    const week = config.hours.week.map((windows, i) =>
      i === day
        ? windows.length > 0
          ? [{ ...windows[0], [field]: minute }]
          : [{ openMinute: 540, closeMinute: 1020, [field]: minute }]
        : windows,
    )
    setHours({ week })
  }

  const toggleDay = (day: number, open: boolean) =>
    setHours({
      week: config.hours.week.map((windows, i) =>
        i === day ? (open ? [{ openMinute: 540, closeMinute: 1020 }] : []) : windows,
      ),
    })

  return (
    <>
      {!canManage && <ReadOnlyNotice what="auto-reply settings" />}
      <header className="stack gap-1">
        <span className="cad-eyebrow">Automation</span>
        <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
          <h1 className="cad-title">Automatic replies</h1>
          <span className="cad-pill" data-tone={config.enabled ? 'good' : undefined}>
            {config.enabled ? 'on' : 'off'}
          </span>
        </div>
        <p className="cad-muted" style={{ maxWidth: '64ch' }}>
          Answer out of hours, greet someone messaging for the first time, or reply to a
          question you have already written the answer to.
        </p>
      </header>

      {/* The guards, stated before anything is switched on. */}
      <section className="cad-card">
        <div className="stack gap-2">
          <h2 className="cad-section-title">What will never happen</h2>
          <p className="cad-note">
            One reply per conversation per {config.cooldownMinutes} minutes, however many
            messages arrive. Nothing is sent while a teammate is already replying. Nothing is
            sent to someone who asked us to stop. If the same message keeps arriving — which is
            what another auto-responder looks like — replying stops rather than continuing all
            night.
          </p>
        </div>
      </section>

      <section className="cad-card">
        <div className="stack gap-4">
          <label className="row gap-2" style={{ alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={config.enabled}
              disabled={!canManage}
              onChange={(e) => set({ enabled: e.target.checked })}
            />
            <b>Reply automatically</b>
          </label>

          {/* ------------------------------------------------ hours */}

          <div className="stack gap-3">
            <div className="row between wrap gap-2" style={{ alignItems: 'baseline' }}>
              <h2 className="cad-section-title">When you are open</h2>
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                {describeHours(config.hours)}
              </span>
            </div>

            <div className="cad-field" style={{ maxWidth: '22rem' }}>
              <label htmlFor="ar-tz">Your timezone</label>
              <input
                id="ar-tz"
                className="cad-input"
                list="ar-zones"
                value={config.hours.timezone}
                disabled={!canManage}
                onChange={(e) => setHours({ timezone: e.target.value })}
              />
              <datalist id="ar-zones">
                {ZONES.map((zone) => (
                  <option key={zone} value={zone} />
                ))}
              </datalist>
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                Hours are read in this zone, so summer time is handled for you.
              </span>
            </div>

            <div className="stack gap-2">
              {config.hours.week.map((windows, day) => (
                <div key={day} className="row gap-2 wrap" style={{ alignItems: 'center' }}>
                  <label
                    className="row gap-2"
                    style={{ alignItems: 'center', cursor: 'pointer', width: '9rem' }}
                  >
                    <input
                      type="checkbox"
                      checked={windows.length > 0}
                      disabled={!canManage}
                      onChange={(e) => toggleDay(day, e.target.checked)}
                    />
                    <span>{DAY_NAMES[day]}</span>
                  </label>
                  {windows.length > 0 ? (
                    <>
                      <input
                        className="cad-input"
                        style={{ width: '6.5rem' }}
                        type="time"
                        aria-label={`${DAY_NAMES[day]} opens`}
                        value={formatMinute(windows[0].openMinute)}
                        disabled={!canManage}
                        onChange={(e) => setWindow(day, 'openMinute', e.target.value)}
                      />
                      <span className="cad-muted">to</span>
                      <input
                        className="cad-input"
                        style={{ width: '6.5rem' }}
                        type="time"
                        aria-label={`${DAY_NAMES[day]} closes`}
                        value={formatMinute(windows[0].closeMinute)}
                        disabled={!canManage}
                        onChange={(e) => setWindow(day, 'closeMinute', e.target.value)}
                      />
                      {/* A kitchen open from six until two is one window, not a mistake. */}
                      {windows[0].closeMinute <= windows[0].openMinute && (
                        <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                          runs past midnight
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="cad-muted">closed</span>
                  )}
                </div>
              ))}
            </div>

            <div className="cad-field">
              <label htmlFor="ar-holidays">Days you are shut — one date per line</label>
              <textarea
                id="ar-holidays"
                className="cad-input"
                rows={2}
                value={config.hours.holidays.join('\n')}
                disabled={!canManage}
                placeholder="2026-08-15"
                onChange={(e) =>
                  setHours({ holidays: e.target.value.split('\n').map((v) => v.trim()).filter(Boolean) })
                }
              />
            </div>
          </div>

          {/* ------------------------------------------------ messages */}

          <div className="cad-field">
            <label htmlFor="ar-away">Out of hours, send</label>
            <textarea
              id="ar-away"
              className="cad-input"
              rows={2}
              value={config.awayMessage ?? ''}
              disabled={!canManage}
              placeholder="Thanks for your message — we are closed and will reply when we open."
              onChange={(e) => set({ awayMessage: e.target.value || null })}
            />
          </div>

          <div className="cad-field">
            <label htmlFor="ar-greeting">The first time someone ever messages, send</label>
            <textarea
              id="ar-greeting"
              className="cad-input"
              rows={2}
              value={config.greeting ?? ''}
              disabled={!canManage}
              placeholder="Thanks for getting in touch — we usually reply within the hour."
              onChange={(e) => set({ greeting: e.target.value || null })}
            />
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
              Sent once, not on every conversation.
            </span>
          </div>

          <div className="stack gap-2">
            <span className="cad-eyebrow">Keyword replies</span>
            {config.keywordReplies.map((rule, index) => (
              <div key={index} className="row gap-2 wrap" style={{ alignItems: 'flex-start' }}>
                <input
                  className="cad-input"
                  style={{ width: '13rem' }}
                  aria-label="Keywords"
                  value={rule.keywords.join(', ')}
                  disabled={!canManage}
                  placeholder="price, how much"
                  onChange={(e) =>
                    set({
                      keywordReplies: config.keywordReplies.map((r, i) =>
                        i === index
                          ? { ...r, keywords: e.target.value.split(',').map((k) => k.trim()) }
                          : r,
                      ),
                    })
                  }
                />
                <textarea
                  className="cad-input"
                  style={{ flex: 1, minWidth: '220px' }}
                  rows={2}
                  aria-label="Reply"
                  value={rule.message}
                  disabled={!canManage}
                  placeholder="Our price list is at example.com/prices."
                  onChange={(e) =>
                    set({
                      keywordReplies: config.keywordReplies.map((r, i) =>
                        i === index ? { ...r, message: e.target.value } : r,
                      ),
                    })
                  }
                />
                {canManage && (
                  <button
                    type="button"
                    className="cad-btn"
                    data-variant="text"
                    onClick={() =>
                      set({ keywordReplies: config.keywordReplies.filter((_, i) => i !== index) })
                    }
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            {canManage && (
              <div>
                <button
                  type="button"
                  className="cad-btn"
                  data-variant="quiet"
                  onClick={() =>
                    set({
                      keywordReplies: [
                        ...config.keywordReplies,
                        { keywords: [], message: '', enabled: true },
                      ],
                    })
                  }
                >
                  Add a keyword reply
                </button>
              </div>
            )}
          </div>

          {/* ------------------------------------------------ knowledge */}

          <div className="stack gap-2">
            <label className="row gap-2" style={{ alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={config.answerFromKnowledge}
                disabled={!canManage || knowledgeCount === 0}
                onChange={(e) => set({ answerFromKnowledge: e.target.checked })}
              />
              <span>Answer from the knowledge base</span>
            </label>
            {knowledgeCount === 0 ? (
              <p className="cad-note">Write some answers first and this becomes available.</p>
            ) : (
              <>
                <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
                  <span className="cad-muted">Only when the match is</span>
                  <select
                    className="cad-input"
                    style={{ width: '11rem' }}
                    aria-label="Confidence needed"
                    value={config.knowledgeConfidence}
                    disabled={!canManage || !config.answerFromKnowledge}
                    onChange={(e) =>
                      set({ knowledgeConfidence: e.target.value as 'likely' | 'strong' })
                    }
                  >
                    <option value="strong">strong</option>
                    <option value="likely">likely or better</option>
                  </select>
                </div>
                {/* Sending an unchecked answer is a different risk to sending office hours. */}
                <p className="cad-note">
                  This sends an answer nobody has read first. Start with <b>strong</b>, watch
                  what goes out below, and lower it only once you trust what you see.
                </p>
              </>
            )}
          </div>

          {/* ------------------------------------------------ AI */}

          <div className="stack gap-2">
            <label className="row gap-2" style={{ alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={config.aiReplies}
                disabled={!canManage || !aiConfigured}
                onChange={(e) => set({ aiReplies: e.target.checked })}
              />
              <span>Draft a reply with AI when nothing above applies</span>
            </label>
            {!aiConfigured ? (
              <p className="cad-note">
                No AI provider is configured yet — add one under AI agents to turn this on.
              </p>
            ) : (
              <p className="cad-note">
                Tried last, and only during open hours: after a greeting, keyword, or knowledge
                answer have all missed. Every chat starts with this on once the setting above is;
                turn it off for one conversation at a time from that chat&rsquo;s details panel.
              </p>
            )}
          </div>

          {/* ------------------------------------------------ limits */}

          <div className="field-grid">
            <div className="cad-field">
              <label htmlFor="ar-cooldown">Wait this long before replying again (minutes)</label>
              <input
                id="ar-cooldown"
                className="cad-input"
                type="number"
                min={1}
                value={config.cooldownMinutes}
                disabled={!canManage}
                onChange={(e) => set({ cooldownMinutes: Number(e.target.value) })}
              />
            </div>
            <div className="cad-field">
              <label htmlFor="ar-human">Stay quiet if a teammate replied within (minutes)</label>
              <input
                id="ar-human"
                className="cad-input"
                type="number"
                min={0}
                value={config.humanWindowMinutes}
                disabled={!canManage}
                onChange={(e) => set({ humanWindowMinutes: Number(e.target.value) })}
              />
            </div>
          </div>

          <label className="row gap-2" style={{ alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={config.replyInGroups}
              disabled={!canManage}
              onChange={(e) => set({ replyInGroups: e.target.checked })}
            />
            <span>
              Also reply in group chats{' '}
              <span className="cad-muted">— everyone in the group sees it</span>
            </span>
          </label>

          {problems.map((problem) => (
            <p key={problem} className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
              {problem}
            </p>
          ))}
          {save.error && (
            <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
              {save.error instanceof Error ? save.error.message : 'That did not save.'}
            </p>
          )}

          {canManage && (
            <div className="row gap-2 wrap">
              <button
                type="button"
                className="cad-btn"
                disabled={!dirty || problems.length > 0 || save.isPending}
                onClick={() => save.mutate(config)}
              >
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
              {dirty && (
                <button
                  type="button"
                  className="cad-btn"
                  data-variant="text"
                  onClick={() => setDraft(null)}
                >
                  Undo changes
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      <Preview config={config} dirty={dirty} />

      {activity.length > 0 && (
        <section className="cad-card">
          <div className="stack gap-3">
            <h2 className="cad-section-title">What it has done</h2>
            <div className="cad-grid-frame">
              <table className="cad-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Sent</th>
                    <th>Why</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.map((entry) => (
                    <tr key={entry.id}>
                      <td className="cad-muted">{new Date(entry.createdAt).toLocaleString()}</td>
                      <td>
                        {entry.sent ? (
                          <span className="cad-pill" data-tone="good">
                            {KIND_LABELS[entry.kind ?? ''] ?? 'sent'}
                          </span>
                        ) : (
                          <span className="cad-pill">nothing</span>
                        )}
                      </td>
                      <td
                        className="cad-muted"
                        style={{ maxWidth: '32ch', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={entry.detail ?? undefined}
                      >
                        {entry.detail}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </>
  )
}

/**
 * Shows what a given message would get.
 *
 * Every guard in this feature is a reason *not* to reply, so a customer who has switched it on
 * and seen nothing happen has no way to tell which one stopped it. This asks the real decision
 * function with the settings on screen, saved or not.
 */
function Preview({ config, dirty }: { config: AutoReplyConfig; dirty: boolean }) {
  const [body, setBody] = useState('')
  const [firstTime, setFirstTime] = useState(false)
  const [when, setWhen] = useState('')

  const preview = useMutation({
    mutationFn: () =>
      api.previewAutoReply({
        body,
        isFirstEverMessage: firstTime,
        at: when ? new Date(when).toISOString() : undefined,
        // Sent along so unsaved edits are what gets tested, not what is stored.
        config: dirty ? config : undefined,
      }),
  })

  return (
    <section className="cad-card">
      <div className="stack gap-3">
        <h2 className="cad-section-title">Try a message</h2>
        <div className="field-grid">
          <div className="cad-field" style={{ gridColumn: '1 / -1' }}>
            <label htmlFor="ar-test">What might a customer send?</label>
            <input
              id="ar-test"
              className="cad-input"
              value={body}
              placeholder="How much is delivery?"
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <div className="cad-field">
            <label htmlFor="ar-when">At (leave blank for now)</label>
            <input
              id="ar-when"
              className="cad-input"
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
          </div>
        </div>

        <label className="row gap-2" style={{ alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={firstTime} onChange={(e) => setFirstTime(e.target.checked)} />
          <span className="cad-muted">They have never messaged before</span>
        </label>

        <div>
          <button
            type="button"
            className="cad-btn"
            disabled={!body.trim() || preview.isPending}
            onClick={() => preview.mutate()}
          >
            {preview.isPending ? 'Checking…' : 'See what happens'}
          </button>
        </div>

        {preview.data && (
          <div className="stack gap-2">
            <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
              <span className="cad-pill" data-tone={preview.data.open ? 'good' : undefined}>
                {preview.data.open ? 'open' : 'closed'}
              </span>
              {preview.data.kind && (
                <span className="cad-pill" data-tone="good">
                  {KIND_LABELS[preview.data.kind] ?? preview.data.kind}
                </span>
              )}
            </div>
            {preview.data.message ? (
              <div className="cad-bubble">{preview.data.message}</div>
            ) : preview.data.pending ? (
              <p className="cad-note">
                Nothing else applies — the AI would draft a reply here. The preview doesn&rsquo;t
                spend a real call to show you the words; a live message will.
              </p>
            ) : (
              <p className="cad-note">{preview.data.reason}</p>
            )}
          </div>
        )}
        {preview.error && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {preview.error instanceof Error ? preview.error.message : 'That did not work.'}
          </p>
        )}
      </div>
    </section>
  )
}

export { DEFAULT_AUTO_REPLY }
